import { v4 as uuid } from "uuid";

import {
  Connection,
  ConnectionConfiguration,
  ConnectionConfigurationCreate,
  ConnectionCreate,
  ResourceConfig,
} from "src/api/connectApi/types";
import {
  filterSecretsByEnvironment,
  filterUpdatedSecrets,
  findSecret,
  getDefaultSecret,
  markSecrets,
  setSecretEnv,
} from "src/connections/model/common";
import {
  InboundWebhookConnectionConfig,
  InboundWebhookConnectionConfigInputs,
  InboundWebhookResourceConfig,
  KeyValuePair,
} from "src/connections/types";
import { assertUnreachable } from "src/utils/typeUtils";

export const isManualReviewNodeConnection = (connection: Connection) =>
  connection.provider === "webhook" &&
  connection.resource_configs.length === 1 &&
  connection.resource_configs[0].resource === "review_event";

export const isCaseCreationNodeConnection = (connection: Connection) =>
  connection.provider === "webhook" &&
  connection.resource_configs.length === 1 &&
  connection.resource_configs[0].resource === "case_event";

export const isCaseUpdateNodeConnection = (connection: Connection) =>
  connection.provider === "webhook" &&
  connection.resource_configs.length === 1 &&
  connection.resource_configs[0].resource === "case_update_event";

export const getInboundWebhookResourceConfigDefaultValues =
  (): InboundWebhookResourceConfig => ({
    id: uuid(),
    resource: "event",
    name: "",
    correlationIDPath: "",
    activation: null,
  });

export const getInboundWebhookConnectionConfigDefaultValues = (
  env: string | null,
): InboundWebhookConnectionConfig => ({
  webhookAuthMethod: "api_key",
  webhookNoAuthConfig: {},
  webhookBasicAuthConfig: {
    login: getDefaultSecret("login", env),
    password: getDefaultSecret("password", env),
  },
  webhookApiKeyConfig: {
    header: null,
    prefix: null,
    apiKey: getDefaultSecret("api_key", env),
  },
  webhookHMACConfig: {
    hashAlgorithm: "sha1",
    signatureEncoding: "hex",
    header: "",
    signingKey: getDefaultSecret("signing_key", env),
  },
  webhookHMACPersonaConfig: {
    signingKey: getDefaultSecret("signing_key", env),
  },
  webhookTruliooHMACHandshakeConfig: {
    signingKey: getDefaultSecret("signing_key", env),
  },
  webhookURL: null,
  allowTestInvocations: false,
});

export const getInboundWebhookConnectionInputsDefaultValues =
  (): InboundWebhookConnectionConfigInputs => ({
    name: "",
    mediaKey: null,
    dataRetention: {
      value: 0,
      unit: "days",
    },
    enableNonProdConfigs: false,
    productionConfig: getInboundWebhookConnectionConfigDefaultValues(null),
    sandboxConfig: getInboundWebhookConnectionConfigDefaultValues("sandbox"),
    customSuccessStatusCode: null,
    resourceConfigs: [getInboundWebhookResourceConfigDefaultValues()],
  });

export const convertBEResourceToInboundWebhookResourceInputs = (
  resource: ResourceConfig,
) =>
  ({
    id: resource.id,
    name: resource.name,
    resource: resource.resource,
    correlationIDPath: resource.configuration.correlation_id_path,
    activation: resource.configuration.activation,
  }) as InboundWebhookResourceConfig;

const convertBEConnectionConfigurationToInboundWebhookConnectionConfig = (
  config: ConnectionConfiguration,
  envSecrets: KeyValuePair[],
  env: string | null,
): InboundWebhookConnectionConfig => {
  const webhookAuthSettings = config.webhook_auth_settings!;
  const convertedConfig = {
    ...getInboundWebhookConnectionConfigDefaultValues(env),
    webhookURL: config.webhook_url ?? null,
    webhookAuthMethod: webhookAuthSettings.auth_method,
    allowTestInvocations: config.allow_test_invocations ?? false,
  };

  switch (webhookAuthSettings.auth_method) {
    case "no_auth":
      return convertedConfig;
    case "basic":
      return {
        ...convertedConfig,
        webhookBasicAuthConfig: {
          login:
            findSecret(envSecrets, "login") ?? getDefaultSecret("login", env),
          password:
            findSecret(envSecrets, "password") ??
            getDefaultSecret("password", env),
        },
      };
    case "api_key":
      return {
        ...convertedConfig,
        webhookApiKeyConfig: {
          header: webhookAuthSettings.header,
          prefix: webhookAuthSettings.prefix,
          apiKey:
            findSecret(envSecrets, "api_key") ??
            getDefaultSecret("api_key", env),
        },
      };
    case "hmac":
      return {
        ...convertedConfig,
        webhookHMACConfig: {
          hashAlgorithm: webhookAuthSettings.hash_algorithm,
          signatureEncoding: webhookAuthSettings.signature_encoding,
          header: webhookAuthSettings.header,
          signingKey:
            findSecret(envSecrets, "signing_key") ??
            getDefaultSecret("signing_key", env),
        },
      };
    case "hmac_persona":
      return {
        ...convertedConfig,
        webhookHMACPersonaConfig: {
          signingKey:
            findSecret(envSecrets, "signing_key") ??
            getDefaultSecret("signing_key", env),
        },
      };
    case "hmac_trulioo_handshake":
      return {
        ...convertedConfig,
        webhookTruliooHMACHandshakeConfig: {
          signingKey:
            findSecret(envSecrets, "signing_key") ??
            getDefaultSecret("signing_key", env),
        },
      };
  }
};

/**
 * Converts BE inbound webhook connection configuration to FE format.
 *
 * @param {Connection} connection - The inbound webhook connection object in BE format.
 * @returns {InboundWebhookConnectionConfigInputs} - The converted inbound webhook connection object in FE format suitable to use with components.
 * @throws {Error} - Throws an error if the provider of the connection is not "webhook".
 */
export const convertBEConnectionToInboundWebhookConnectionConfigInputs = (
  connection: Connection,
): InboundWebhookConnectionConfigInputs => {
  if (connection.provider !== "webhook")
    throw new Error(
      `Invalid connection type: ${connection.provider} for Inbound Webhook converter`,
    );

  const defaultValues = getInboundWebhookConnectionInputsDefaultValues();
  const nonProdEnvConfigs = connection.non_prod_env_configs;

  const allSecrets = markSecrets(connection.secrets);
  const prodSecrets = filterSecretsByEnvironment(allSecrets, null);
  const sandboxSecrets = filterSecretsByEnvironment(allSecrets, "sandbox");

  const isSandboxEnabled =
    connection.enable_non_prod_configs &&
    nonProdEnvConfigs &&
    nonProdEnvConfigs["sandbox"];
  return {
    ...defaultValues,
    name: connection.name,
    mediaKey: connection.configuration.media_key ?? null,
    customSuccessStatusCode:
      connection.configuration.custom_success_status_code ?? null,
    dataRetention: connection.data_retention ?? defaultValues.dataRetention,
    enableNonProdConfigs: connection.enable_non_prod_configs,
    productionConfig:
      convertBEConnectionConfigurationToInboundWebhookConnectionConfig(
        connection.configuration,
        prodSecrets,
        null,
      ),
    sandboxConfig: isSandboxEnabled
      ? convertBEConnectionConfigurationToInboundWebhookConnectionConfig(
          nonProdEnvConfigs["sandbox"],
          sandboxSecrets,
          "sandbox",
        )
      : getInboundWebhookConnectionConfigDefaultValues("sandbox"),

    resourceConfigs: connection.resource_configs.map(
      convertBEResourceToInboundWebhookResourceInputs,
    ),
  };
};

const convertInboundWebhookAuthSettingsToBEConfig = (
  config: InboundWebhookConnectionConfig,
) => {
  const { webhookAuthMethod, webhookApiKeyConfig, webhookHMACConfig } = config;

  const base = {
    auth_method: webhookAuthMethod,
  };

  // Stripping the secrets from the BE config format
  switch (webhookAuthMethod) {
    case "basic":
      return base;
    case "no_auth":
      return base;
    case "api_key":
      return {
        ...base,
        header: webhookApiKeyConfig.header,
        prefix: webhookApiKeyConfig.prefix,
      };
    case "hmac":
      return {
        ...base,
        hash_algorithm: webhookHMACConfig.hashAlgorithm,
        signature_encoding: webhookHMACConfig.signatureEncoding,
        header: webhookHMACConfig.header,
      };
    case "hmac_persona":
      return base;
    case "hmac_trulioo_handshake":
      return base;
    default:
      return assertUnreachable(webhookAuthMethod);
  }
};

const extractSecretsFromInboundWebhookConnectionConfig = (
  config: InboundWebhookConnectionConfig,
  env: string | null,
): KeyValuePair[] => {
  const {
    webhookAuthMethod,
    webhookBasicAuthConfig,
    webhookApiKeyConfig,
    webhookHMACConfig,
    webhookHMACPersonaConfig,
    webhookTruliooHMACHandshakeConfig,
  } = config;

  switch (webhookAuthMethod) {
    case "basic":
      return [
        setSecretEnv(webhookBasicAuthConfig.login, env),
        setSecretEnv(webhookBasicAuthConfig.password, env),
      ];
    case "api_key":
      return [setSecretEnv(webhookApiKeyConfig.apiKey, env)];
    case "hmac":
      return [setSecretEnv(webhookHMACConfig.signingKey, env)];
    case "hmac_persona":
      return [setSecretEnv(webhookHMACPersonaConfig.signingKey, env)];
    case "hmac_trulioo_handshake":
      return [setSecretEnv(webhookTruliooHMACHandshakeConfig.signingKey, env)];
    default:
      return [];
  }
};

const extractSecretsFromInboundWebhookConnectionConfigInputs = (
  config: InboundWebhookConnectionConfigInputs,
): KeyValuePair[] => {
  const secrets = extractSecretsFromInboundWebhookConnectionConfig(
    config.productionConfig,
    null,
  );
  // Only include non-prod secrets if non-prod configs are enabled
  if (config.enableNonProdConfigs && config.sandboxConfig) {
    secrets.push(
      ...extractSecretsFromInboundWebhookConnectionConfig(
        config.sandboxConfig,
        "sandbox",
      ),
    );
  }
  // Removing un-changed secrets, so that they are not sent to the backend
  return filterUpdatedSecrets(secrets);
};

const convertInboundWebhookInputConfigurationToCreateConnection = (
  configuration: InboundWebhookConnectionConfig,
  custom_success_status_code: number | null,
): ConnectionConfigurationCreate => ({
  webhook_auth_settings:
    convertInboundWebhookAuthSettingsToBEConfig(configuration),
  allow_test_invocations: configuration.allowTestInvocations,
  custom_success_status_code,
});

const convertInboundWebhookActivationInputsToActivation = (
  activation: InboundWebhookResourceConfig["activation"],
) => {
  // We make sure that activation without clauses is converted to null
  if (activation == null) return null;
  if (activation.clauses && activation.clauses.length === 0) return null;
  return activation as unknown as ResourceConfig["configuration"]["activation"];
};

const inboundWebhookConnectionInputsToResources = (
  configConnectionForm: InboundWebhookConnectionConfigInputs,
) =>
  configConnectionForm.resourceConfigs.map((resourceConfig) => {
    return {
      id: resourceConfig.id,
      name: resourceConfig.name,
      resource: resourceConfig.resource,
      configuration: {
        correlation_id_path: resourceConfig.correlationIDPath,
        activation: convertInboundWebhookActivationInputsToActivation(
          resourceConfig.activation,
        ),
      },
      has_raw_response_enabled: false,
      created_at: "",
      updated_at: "",
    };
  });

/**
 * Converts FE inbound webhook connection configuration to BE format.
 * @param {InboundWebhookConnectionConfigInputs} inputs - The inbound webhook connection object in FE format.
 * @returns {ConnectionCreate} - The inbound webhook connection object in BE format suitable for sending POST request.
 */
export const convertInboundWebhookConnectionConfigInputsToBEConnection = (
  inputs: InboundWebhookConnectionConfigInputs,
): ConnectionCreate => {
  const secrets =
    extractSecretsFromInboundWebhookConnectionConfigInputs(inputs);

  return {
    name: inputs.name,
    is_sandbox: false,
    provider: "webhook",
    secrets,
    data_retention: inputs.dataRetention,
    configuration: {
      ...convertInboundWebhookInputConfigurationToCreateConnection(
        inputs.productionConfig,
        inputs.customSuccessStatusCode,
      ),
      media_key: inputs.mediaKey ?? null,
    },
    enable_non_prod_configs: inputs.enableNonProdConfigs,
    non_prod_env_configs:
      inputs.enableNonProdConfigs && inputs.sandboxConfig
        ? {
            sandbox: convertInboundWebhookInputConfigurationToCreateConnection(
              inputs.sandboxConfig,
              inputs.customSuccessStatusCode,
            ),
          }
        : {},
    resource_configs: inboundWebhookConnectionInputsToResources(inputs),
  };
};
